import { renderSavedCards } from './saved-card-picker.js';
import { formatCurrency } from '../pledge-pay/pledge-pay-utils.js';
import { setDurationControlMax } from './duration-control.js';
import {
    AMOUNT_MODE_WHOLE,
    lastInstallmentAmount,
    maxPaymentsForMinimum,
    money,
    planRecurringEdit,
} from './recurring-edit-plan.js';

export const SCHEDULE_EDIT_TARGET_REMAINING = 'remaining';
export const SCHEDULE_EDIT_TARGET_COUNT = 'payments_left';

function parseMoney(value) {
    return money(String(value || '').replace(/,/g, ''));
}

export function setEditRecurringFormError($modal, message) {
    const el = $modal.find('#edit_recurring_form_error').get(0);
    if (!el) {
        return;
    }
    el.textContent = message || '';
    el.hidden = !message;
    if (message) {
        el.focus();
        if (typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ block: 'nearest' });
        }
    }
}

export function clearEditRecurringFormError($modal) {
    setEditRecurringFormError($modal, '');
}

export function selectedScheduleEditTarget($modal) {
    return String($modal.find('input[name="edit_recurring_change"]:checked').val() || '');
}

function thisPledgeRemainingFromModal($modal) {
    const $amount = $modal.find('#pledge_amount_input');
    const totalsHidden = $modal.find('#pledge_totals_row').hasClass('d-none');
    if (!$amount.length || totalsHidden) {
        return parseMoney($modal.find('#on_amount').val());
    }
    const amount = parseMoney($amount.val());
    const paid = parseMoney($amount.data('amountPaid'));
    return Math.max(0, amount - paid);
}

export function remainingBalanceFromModal($modal) {
    return money(thisPledgeRemainingFromModal($modal))
        + money($modal.data('editRecurringOtherScheduleRemaining') || 0);
}

function otherScheduleRemaining($modal) {
    return money($modal.data('editRecurringOtherScheduleRemaining') || 0);
}

function describeLock($el, reasonId, reason) {
    const el = $el.get(0);
    if (!el) {
        return;
    }
    let note = document.getElementById(reasonId);
    if (!note && reason) {
        note = document.createElement('span');
        note.id = reasonId;
        note.className = 'sr-only';
        el.appendChild(note);
    }
    if (note) {
        note.textContent = reason;
        note.hidden = !reason;
    }
    if (reason) {
        el.setAttribute('aria-describedby', reasonId);
        el.setAttribute('aria-disabled', 'true');
        return;
    }
    el.removeAttribute('aria-describedby');
    el.removeAttribute('aria-disabled');
}

export function isFinitePledgeRecurringEdit($modal) {
    return $modal.data('ongoingType') === 'Pledge' && !$modal.find('#pledge_totals_row').hasClass('d-none');
}

function setPanelVisible($panel, visible) {
    const panel = $panel.get(0);
    if (!panel) {
        return;
    }
    panel.hidden = !visible;
    if (panel.id === 'edit_recurring_editor') {
        $panel.closest('#edit_recurring_balance_panel').toggleClass('has-editor', visible);
    }
    if (panel.id === 'edit_recurring_updated_panel') {
        $panel.closest('#pledge_totals_row').toggleClass('has-updated-plan', visible);
        if (!visible) {
            $panel.find('[data-plan-metric]').removeClass('is-changed');
        }
    }
}

function currentInstallment($modal) {
    return parseMoney($modal.find('#on_amount').val());
}

function currentNumLeft($modal) {
    return parseInt($modal.find('#on_num_left').val(), 10) || 0;
}

function frequencyMaxInstallments($modal) {
    const config = $modal.data('editOngoingConfig') || {};
    const map = config.frequencyMaxInstallments || {};
    return Number(map[String($modal.find('#on_frequency_id').val() || '')]);
}

function maxPaymentsAllowed($modal, minTransAmt) {
    const byMinimum = maxPaymentsForMinimum(remainingBalanceFromModal($modal), minTransAmt);
    const byFrequency = frequencyMaxInstallments($modal);
    const caps = [];
    if (byMinimum >= 1) {
        caps.push(byMinimum);
    }
    if (Number.isFinite(byFrequency) && byFrequency >= 1) {
        caps.push(byFrequency);
    }
    if (!caps.length) {
        return 0;
    }
    return Math.min(...caps);
}

function applyCountInputMaxAttribute($modal, minTransAmt) {
    const max = maxPaymentsAllowed($modal, minTransAmt);
    const field = $modal.find('#edit_recurring_count_input').get(0);
    if (field) {
        field.min = '1';
        if (max >= 1) {
            field.max = String(max);
        } else {
            field.removeAttribute('max');
        }
    }
    return max;
}

function durationValueEl($modal) {
    return $modal.find('#on_num_left').get(0);
}

function capDurationControl($modal, max) {
    const value = durationValueEl($modal);
    if (value && value.closest('[data-duration-control]')) {
        setDurationControlMax(value, max);
    }
}

function clampPaymentCountToMax($modal, minTransAmt) {
    const max = applyCountInputMaxAttribute($modal, minTransAmt);
    capDurationControl($modal, max);
    if (max < 1 || currentNumLeft($modal) <= max) {
        return;
    }
    $modal.find('#on_num_left').val(String(max));
    const field = $modal.find('#edit_recurring_count_input').get(0);
    if (field) {
        field.value = String(max);
    }
}

function currentPledgeAmount($modal) {
    return parseMoney($modal.find('#pledge_amount_input').val());
}

export function clearScheduleEditTarget($modal) {
    $modal.find('input[name="edit_recurring_change"]').prop('checked', false);
}

export function captureEditRecurringBaseline($modal) {
    $modal.data('editRecurringInitialRemaining', remainingBalanceFromModal($modal));
    $modal.data('editRecurringBaselineInstallment', currentInstallment($modal));
    $modal.data('editRecurringBaselineNumLeft', currentNumLeft($modal));
    $modal.data('editRecurringBaselinePledgeAmount', currentPledgeAmount($modal));
    $modal.data('editRecurringBaselineThisRemaining', thisPledgeRemainingFromModal($modal));
}

export function remainingBalanceChangedFromBaseline($modal) {
    return remainingBalanceFromModal($modal) !== parseMoney($modal.data('editRecurringInitialRemaining'));
}

function splitSourceInstallment($modal) {
    return parseMoney($modal.data('editRecurringBaselineInstallment'));
}

function splitSourceNumLeft($modal) {
    return parseInt($modal.data('editRecurringBaselineNumLeft'), 10) || 0;
}

export function restoreEditRecurringBaseline($modal) {
    $modal.data('editRecurringApplyingMode', true);
    const pledgeAmount = $modal.data('editRecurringBaselinePledgeAmount');
    if (pledgeAmount !== undefined && $modal.find('#pledge_amount_input').length) {
        $modal.find('#pledge_amount_input').val(formatCurrency(parseMoney(pledgeAmount)));
    }
    $modal.find('#on_num_left').val(String(splitSourceNumLeft($modal)));
    $modal.find('#on_num_left').trigger('change');
    $modal.find('#on_amount').val(formatCurrency(splitSourceInstallment($modal)));
    $modal.removeData('editRecurringApplyingMode');
    syncInstallmentWidget($modal);
}

export function applyRemainingInputFromField($modal) {
    const field = $modal.find('#edit_recurring_remaining_input').get(0);
    if (!field) {
        return;
    }
    const paid = parseMoney($modal.find('#pledge_amount_input').data('amountPaid'));
    $modal.find('#pledge_amount_input').val(formatCurrency(money(paid + parseMoney(field.value))));
}

export function syncOngoingEditFromNumLeft($modal) {
    const data = $modal.data('editRecurringPledgeData');
    if (!data) {
        return null;
    }
    const ongoing = currentNumLeft($modal) === 0;
    data.ongoing_indefinitely = ongoing;
    if (!ongoing && $modal.data('ongoingType') === 'Pledge') {
        const each = currentInstallment($modal);
        const count = currentNumLeft($modal);
        if (each > 0 && count >= 1) {
            const remaining = money(each * count);
            const paid = parseMoney($modal.find('#pledge_amount_input').data('amountPaid'));
            data.balance_due = remaining;
            data.amount = money(paid + remaining);
            data.installment_amount = each;
            $modal.find('#pledge_amount_input').val(formatCurrency(data.amount));
        }
    }
    $modal.data('editRecurringPledgeData', data);
    return data;
}

export function applyCountInputFromField($modal) {
    const field = $modal.find('#edit_recurring_count_input').get(0);
    if (!field) {
        return;
    }
    let count = parseInt(field.value, 10);
    if (!Number.isInteger(count) || count < 1) {
        return;
    }
    const max = maxPaymentsAllowed($modal, Number($modal.data('editOngoingMinTransAmt')) || 0);
    if (max >= 1 && count > max) {
        count = max;
        field.value = String(max);
    }
    $modal.find('#on_num_left').val(String(count)).trigger('change');
}

function setText($modal, selector, value) {
    const el = $modal.find(selector).get(0);
    if (el) {
        el.textContent = value;
    }
}

function moneyLabel(amount) {
    return formatCurrency(amount);
}

function countLabel(count) {
    return count === 0 ? 'Ongoing' : String(count);
}

function paintLastPaymentNote($modal, prefix, remaining, each, count) {
    const el = $modal.find(`#edit_recurring_${prefix}_last`).get(0);
    if (!el) {
        return;
    }
    const last = lastInstallmentAmount(remaining, each, count);
    const show = count > 1 && last > 0 && money(last) !== money(each);
    el.hidden = !show;
    el.textContent = show ? `Last payment $${moneyLabel(last)}` : '';
}

function paintPlanFigures($modal, prefix, figures) {
    setText($modal, `#edit_recurring_${prefix}_each`, moneyLabel(figures.each));
    setText($modal, `#edit_recurring_${prefix}_count`, countLabel(figures.count));
    setText($modal, `#edit_recurring_${prefix}_remaining`, moneyLabel(figures.remaining));
    paintLastPaymentNote($modal, prefix, figures.scheduleRemaining, figures.each, figures.count);
}

function setUpdatedMetricChanged($modal, metric, changed) {
    $modal.find(`#edit_recurring_updated_panel [data-plan-metric="${metric}"]`).toggleClass('is-changed', changed);
}

function paintUpdatedPlan($modal, figures) {
    paintPlanFigures($modal, 'after', figures);
    setUpdatedMetricChanged($modal, 'each', money(figures.each) !== money(splitSourceInstallment($modal)));
    setUpdatedMetricChanged($modal, 'count', figures.count !== splitSourceNumLeft($modal));
    setUpdatedMetricChanged(
        $modal,
        'remaining',
        money(figures.remaining) !== parseMoney($modal.data('editRecurringBaselineThisRemaining'))
    );
    setPanelVisible($modal.find('#edit_recurring_updated_panel'), true);
}

function setModeNotice($modal, message) {
    const notice = $modal.find('#edit_recurring_mode_notice').get(0);
    if (!notice) {
        return;
    }
    notice.textContent = message;
    notice.hidden = message === '';
}

export function applySimpleRecurringPlan($modal, minTransAmt) {
    if (!isFinitePledgeRecurringEdit($modal)) {
        return true;
    }
    const target = selectedScheduleEditTarget($modal);
    if (!target) {
        return true;
    }

    const remaining = remainingBalanceFromModal($modal);
    const numLeft = currentNumLeft($modal);
    const plan = planRecurringEdit(
        AMOUNT_MODE_WHOLE,
        remaining,
        numLeft,
        splitSourceInstallment($modal),
        minTransAmt
    );
    if (plan.error) {
        setModeNotice($modal, plan.error);
        setPanelVisible($modal.find('#edit_recurring_updated_panel'), false);
        return false;
    }

    setModeNotice($modal, '');
    $modal.data('editRecurringApplyingMode', true);
    $modal.find('#on_num_left').val(String(plan.numLeft));
    $modal.find('#on_num_left').trigger('change');
    $modal.find('#on_amount').val(formatCurrency(plan.installmentAmount));
    $modal.removeData('editRecurringApplyingMode');

    paintUpdatedPlan($modal, {
        each: plan.installmentAmount,
        count: plan.numLeft,
        remaining: thisPledgeRemainingFromModal($modal),
        scheduleRemaining: remainingBalanceFromModal($modal),
    });
    return true;
}

export function syncInstallmentWidget($modal) {
    $modal.find('#pledge_amount_display').text(formatCurrency(parseMoney($modal.find('#pledge_amount_input').val())));

    const baselineNumLeft = splitSourceNumLeft($modal);
    paintPlanFigures($modal, 'now', {
        each: splitSourceInstallment($modal),
        count: baselineNumLeft,
        remaining: parseMoney($modal.data('editRecurringBaselineThisRemaining')),
        scheduleRemaining: money(parseMoney($modal.data('editRecurringBaselineThisRemaining')))
            + otherScheduleRemaining($modal),
    });

    const remainingInput = $modal.find('#edit_recurring_remaining_input').get(0);
    if (remainingInput && document.activeElement !== remainingInput) {
        remainingInput.value = formatCurrency(thisPledgeRemainingFromModal($modal));
    }
    const countInput = $modal.find('#edit_recurring_count_input').get(0);
    if (countInput && document.activeElement !== countInput) {
        countInput.value = String(currentNumLeft($modal) || baselineNumLeft);
    }
    applyCountInputMaxAttribute($modal, Number($modal.data('editOngoingMinTransAmt')) || 0);

    const paid = parseMoney($modal.find('#pledge_amount_input').data('amountPaid'));
    const paidNote = $modal.find('#edit_recurring_paid_note').get(0);
    if (paidNote) {
        paidNote.hidden = paid <= 0;
        paidNote.textContent = paid <= 0 ? '' : `Already paid $${moneyLabel(paid)}`;
    }

    const other = otherScheduleRemaining($modal);
    const linkedNote = $modal.find('#edit_recurring_linked_note').get(0);
    if (linkedNote) {
        const list = $modal.find('#edit_recurring_linked_pledges').get(0);
        const count = list ? list.querySelectorAll('li').length : 0;
        linkedNote.hidden = other <= 0;
        linkedNote.textContent = other <= 0
            ? ''
            : `Plus $${moneyLabel(other)} on ${count || 'other'} other pledge${count === 1 ? '' : 's'}`;
    }
}

const LOCK_REASON_PLEDGE_TOTAL_ONGOING = 'Ongoing pledges do not have a fixed total.';
const LOCK_REASON_WAIT = 'Choose Balance or Payments.';
const LOCK_REASON_DERIVED = 'This value comes from the new plan.';

function setLockHint($wrap, locked, reason) {
    if (!$wrap.length) {
        return;
    }
    $wrap.toggleClass('is-locked', locked);
    $wrap.find('[data-edit-recurring-widget]').prop('hidden', !locked);
    $wrap.find('[data-edit-recurring-field]').prop('hidden', locked);
    $wrap.find('[data-edit-recurring-field] input, [data-edit-recurring-field] select, [data-edit-recurring-field] button').prop('disabled', locked);
    const reasonId = `edit_recurring_lock_reason_${String($wrap.attr('data-edit-recurring-lock') || '').replace(/\W+/g, '_')}`;
    describeLock($wrap, reasonId, locked ? reason : '');
}

export function syncEditRecurringDisabledHints($modal) {
    const data = $modal.data('editRecurringPledgeData') || {};
    const isPledge = $modal.data('ongoingType') === 'Pledge';
    const ongoing = !!data.ongoing_indefinitely;
    if (!isFinitePledgeRecurringEdit($modal)) {
        setLockHint($modal.find('[data-edit-recurring-lock="pledge-total"]'), isPledge && ongoing, LOCK_REASON_PLEDGE_TOTAL_ONGOING);
        setLockHint($modal.find('[data-edit-recurring-lock="duration"]'), false, '');
        setLockHint($modal.find('[data-edit-recurring-lock="installment"]'), false, '');
        $modal.find('#edit_recurring_duration_row').prop('hidden', false);
        return;
    }

    const target = selectedScheduleEditTarget($modal);
    setLockHint($modal.find('[data-edit-recurring-lock="pledge-total"]'), target !== SCHEDULE_EDIT_TARGET_REMAINING, LOCK_REASON_WAIT);
    setLockHint($modal.find('[data-edit-recurring-lock="duration"]'), true, LOCK_REASON_DERIVED);
    setLockHint($modal.find('[data-edit-recurring-lock="installment"]'), true, LOCK_REASON_DERIVED);
    $modal.find('#edit_recurring_duration_row').prop('hidden', true);
}

export function syncLinkedPledgesList($modal, data) {
    const list = $modal.find('#edit_recurring_linked_pledges').get(0);
    if (!list) {
        return;
    }
    list.replaceChildren();
    const pledges = data && data.recurring_schedule && Array.isArray(data.recurring_schedule.pledges)
        ? data.recurring_schedule.pledges
        : [];
    const thisId = Number(data && data.id);
    const others = pledges.filter((pledge) => Number(pledge.id) !== thisId && money(pledge.balance_due) > 0);
    if (!others.length) {
        list.hidden = true;
        return;
    }
    others.forEach((pledge) => {
        const item = document.createElement('li');
        item.textContent = `#${pledge.id}  $${formatCurrency(pledge.balance_due)}`;
        list.appendChild(item);
    });
    list.hidden = true;
}

export function updateEditRecurringModePanel($modal, minTransAmt) {
    if (!isFinitePledgeRecurringEdit($modal)) {
        setPanelVisible($modal.find('#edit_recurring_editor'), false);
        setPanelVisible($modal.find('#edit_recurring_updated_panel'), false);
        $modal.find('.edit-recurring-modal__installment').prop('hidden', false);
        $modal.find('#edit_recurring_duration_row').prop('hidden', false);
        syncEditRecurringDisabledHints($modal);
        return;
    }

    const target = selectedScheduleEditTarget($modal);
    const editRemaining = target === SCHEDULE_EDIT_TARGET_REMAINING;
    const editCount = target === SCHEDULE_EDIT_TARGET_COUNT;
    setPanelVisible($modal.find('#edit_recurring_editor'), editRemaining || editCount);
    $modal.find('#edit_recurring_remaining_field').prop('hidden', !editRemaining);
    $modal.find('#edit_recurring_count_field').prop('hidden', !editCount);
    $modal.find('.edit-recurring-modal__installment').prop('hidden', true);
    $modal.find('#edit_recurring_duration_row').prop('hidden', true);

    syncEditRecurringDisabledHints($modal);
    if (editCount) {
        clampPaymentCountToMax($modal, minTransAmt);
    } else {
        capDurationControl($modal, Infinity);
    }
    syncInstallmentWidget($modal);
    if (target) {
        applySimpleRecurringPlan($modal, minTransAmt);
    } else {
        setPanelVisible($modal.find('#edit_recurring_updated_panel'), false);
    }
}

export function setEditRecurringLayout($modal, pledgeType, data) {
    const isPledge = pledgeType === 'Pledge';
    const ongoing = !!(data && data.ongoing_indefinitely);
    const showModes = isPledge && !ongoing;
    $modal.find('.edit-recurring-modal__setup').toggleClass('is-finite', showModes);
    $modal.find('#edit_recurring_balance_panel').prop('hidden', !showModes);
    $modal.find('#pledge_totals_row').toggleClass('d-none', !showModes);
    $modal.find('.edit-recurring-modal__installment').prop('hidden', showModes);
    $modal.find('#edit_recurring_duration_row').prop('hidden', showModes);
    clearScheduleEditTarget($modal);
    clearEditRecurringFormError($modal);
    setPanelVisible($modal.find('#edit_recurring_editor'), false);
    setPanelVisible($modal.find('#edit_recurring_updated_panel'), false);
}

const MANUAL_CARD_INPUTS = '#er_name, #er_card, #er_expiry_month, #er_expiry_year, #er_cvv2, #er_avs_zip';

export function setManualCardCollapsed($modal, collapsed) {
    const $panel = $modal.find('[data-edit-recurring-manual-card]');
    $panel.prop('hidden', collapsed);
    if (collapsed) {
        $modal.find(MANUAL_CARD_INPUTS).val('');
        $modal.find('#er_use_otp').prop('checked', false);
    }
}

export function currentSavedCardIds($modal) {
    const root = $modal && $modal[0];
    if (!root) {
        return [];
    }
    return Array.from(root.querySelectorAll('[data-saved-card-picker="edit_recurring_cc_id"] [data-saved-card-id]'))
        .map((el) => String(el.getAttribute('data-saved-card-id') || '').replace(/\D/g, ''))
        .filter(Boolean);
}

export function addedSavedCardId(cards, previousIds) {
    const prev = new Set(previousIds);
    const added = (cards || []).filter((card) => !prev.has(String(card.id).replace(/\D/g, '')));
    if (!added.length) {
        return '';
    }
    return String(added[added.length - 1].id).replace(/\D/g, '');
}

function resolveSelectedCardId(cards, data, pledgeType) {
    const stored = Number(data?.payment_method_id || 0);
    if (!stored) {
        return '';
    }
    const list = cards || [];
    const byLocalId = list.find((card) => Number(card.id) === stored);
    if (byLocalId) {
        return String(byLocalId.id);
    }
    const byGatewayId = list.find((card) => Number(card.payment_id) === stored);
    if (byGatewayId) {
        return String(byGatewayId.id);
    }
    return pledgeType === 'Donation' ? '' : String(stored);
}

export function loadEditRecurringSavedCards($modal, savedPaymentsUrl, userId, data, pledgeType, selectedId) {
    const requestSeq = (Number($modal.data('editRecurringCardsRequestSeq')) || 0) + 1;
    $modal.data('editRecurringCardsRequestSeq', requestSeq);
    const isCurrent = () => $modal.data('editRecurringCardsRequestSeq') === requestSeq;

    const url = String(savedPaymentsUrl || '').replace(':user_id', encodeURIComponent(String(userId || '')));
    if (!url || url.includes(':user_id') || !userId) {
        renderSavedCards('edit_recurring_cc_id', []);
        return Promise.resolve([]);
    }
    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then((response) => response.json())
        .then((payload) => {
            const cards = payload && payload.success && Array.isArray(payload.saved_cards)
                ? payload.saved_cards
                : [];
            if (!isCurrent()) {
                return cards;
            }
            const chosen = selectedId
                ? selectedId
                : resolveSelectedCardId(cards, data, pledgeType);
            renderSavedCards('edit_recurring_cc_id', cards, chosen);
            return cards;
        })
        .catch(() => {
            if (isCurrent()) {
                renderSavedCards('edit_recurring_cc_id', []);
            }
            return [];
        });
}
